import { Link, Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { env } from "@/utils/env";
import ScrollToTop from "@/components/common/ScrollToTop";
import { NeedHelpDropdown } from "@/components/common/NeedHelpDropdown";
import { PencomRoutes } from "@/router/PencomRoutes";
import PencomLogo from "./PencomLogo";
import { PencomFooter } from "./PencomFooter";

const LOGIN_LOGO = "/assets/images/onboarding/pencom_logo_big.png";
const LEFT_PANEL_IMAGE = "/assets/images/onboarding/onboarding_1.png";

const OnboardingLayout = () => {
  const location = useLocation();
  const getPageTitle = () => {
    switch (location.pathname) {
      case "/onboarding/welcome":
        return "Welcome - Onboarding";
      case PencomRoutes.LOGIN:
        return "Login - PenCom Employer Hub";
      case "/onboarding/verification":
        return "Verification - Onboarding";
      default:
        return "Onboarding";
    }
  };

  return (
    <>
      <ScrollToTop />
      <Helmet>
        <title>{`${getPageTitle()} - ${env.APP_NAME}`}</title>
        <meta name="description" content={`Employee Hub Onboarding`} />
      </Helmet>

      <div className="min-h-screen flex bg-off-white">
        {/* Left panel - show only on extra-large screens to keep tablets focused on the form */}
        <div className="hidden xl:flex xl:w-[620px] 2xl:w-1/2 relative overflow-hidden shrink-0">
          <img
            src={LEFT_PANEL_IMAGE}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-75"
            loading="lazy"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(179.8deg, rgba(255, 255, 255, 0) 12.031%, rgba(7, 4, 4, 0.792) 67.801%)",
            }}
          />
          <div className="relative z-10 flex flex-col min-h-full w-full">
            <PencomLogo />
            <div className="flex flex-col items-start justify-end flex-1 px-10 pt-0 pb-[16vh] text-white">
              <h1 className="text-[32px] font-semibold font-clash-display mb-4 text-white leading-tight">
                Official platform for PCC application and pension compliance
              </h1>
              <p className="text-base font-medium text-white/90 leading-relaxed font-clash-display">
                The Employer Hub (eHub) enables employers to apply for Pension
                Clearance Certificates (PCC), view contribution confirmations,
                and stay aligned with PenCom requirements — through one secure
                government platform.
              </p>
            </div>
            <p className="font-normal font-clash-display text-sm text-white/80 tracking-[-0.06px] pl-[27px] pb-6 shrink-0">
              © {new Date().getFullYear()} NATIONAL PENSION COMMISSION
            </p>
          </div>
        </div>

        {/* Right panel */}
        <div className="flex flex-1 flex-col bg-off-white min-h-screen relative px-4 sm:px-6">
          <img
            src={LOGIN_LOGO}
            alt=""
            className="absolute inset-0 w-full h-full object-contain pointer-events-none z-10"
            loading="lazy"
            aria-hidden
          />

          <div className="flex flex-1 items-center justify-center w-full">
            <div className="w-full max-w-[507px] relative z-10">
              {location.pathname === PencomRoutes.LOGIN ? (
                <div className="flex items-center justify-between gap-x-4 mb-6">
                  <Link
                    to={PencomRoutes.VERIFY_COMPLIANCE}
                    className="inline-flex items-center gap-x-1.5 font-clash-display text-md font-semibold text-primary-button hover:opacity-90"
                  >
                    <svg
                      className="w-4 h-4 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      <path d="m9 12 2 2 4-4" />
                    </svg>
                    <span className="underline underline-offset-2">
                      Verify a company&apos;s compliance status
                    </span>
                  </Link>
                  <NeedHelpDropdown />
                </div>
              ) : (
                <NeedHelpDropdown className="mb-6" />
              )}
              <Outlet />
            </div>
          </div>

          <div className="relative z-10">
            <PencomFooter />
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingLayout;
